import React from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { Label } from "reactstrap";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ThemeProvider } from "@mui/material/styles";
import { parseISO, format, isBefore, isAfter, startOfDay, isValid } from "date-fns";
import { muiTheme } from "src/configs/muiTheme";
import { DATE_FORMAT } from "src/constant/common";

const DatePickerCustomField = ({
  label,
  name,
  placeholder = "yyyy/MM/dd",
  classInput,
  classGroup,
  control,
  defaultValue = null,
  errors,
  required,
  requiredMessage,
  disabled = false,
  maxDate = null,
  minDate = null,
  onChangeExtra,
}) => {
  const { t } = useTranslation("common");

  const parseDate = (dateValue) => {
    if (!dateValue) return null;
    if (dateValue instanceof Date) return isValid(dateValue) ? dateValue : null;
    if (typeof dateValue === "string") {
      try {
        const parsed = parseISO(dateValue);
        return isValid(parsed) ? parsed : dateValue;
      } catch {
        return dateValue;
      }
    }
    return null;
  };

  return (
    <div className={classNames("form-group position-relative", classGroup)}>
      {label && (
        <Label for={name} className={classNames("position-relative", { "required-input": required })}>
          {label}
        </Label>
      )}

      <ThemeProvider theme={muiTheme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Controller
            control={control}
            defaultValue={defaultValue}
            name={name}
            rules={{
              ...(required && { required: requiredMessage || t("required") }),
            }}
            render={({ field: { onChange, value, ref } }) => (
              <DatePicker
                inputRef={ref}
                value={parseDate(value)}
                onChange={(date) => {
                  const formattedDate = date && isValid(date) ? format(date, DATE_FORMAT.DATE) : null;
                  onChange(formattedDate);
                  if (onChangeExtra) onChangeExtra(formattedDate);
                }}
                format="yyyy/MM/dd"
                disabled={disabled}
                minDate={parseDate(minDate)}
                maxDate={parseDate(maxDate)}
                shouldDisableDate={(date) => {
                  const parsedMinDate = parseDate(minDate);
                  const parsedMaxDate = parseDate(maxDate);

                  if (parsedMinDate && isBefore(startOfDay(date), startOfDay(parsedMinDate))) {
                    return true;
                  }
                  if (parsedMaxDate && isAfter(startOfDay(date), startOfDay(parsedMaxDate))) {
                    return true;
                  }
                  return false;
                }}
                shouldDisableMonth={(date) => {
                  const parsedMinDate = parseDate(minDate);
                  const parsedMaxDate = parseDate(maxDate);

                  if (parsedMinDate && date.getFullYear() < parsedMinDate.getFullYear()) {
                    return true;
                  }
                  if (parsedMinDate && date.getFullYear() === parsedMinDate.getFullYear() && date.getMonth() < parsedMinDate.getMonth()) {
                    return true;
                  }
                  if (parsedMaxDate && date.getFullYear() > parsedMaxDate.getFullYear()) {
                    return true;
                  }
                  if (parsedMaxDate && date.getFullYear() === parsedMaxDate.getFullYear() && date.getMonth() > parsedMaxDate.getMonth()) {
                    return true;
                  }
                  return false;
                }}
                shouldDisableYear={(date) => {
                  const parsedMinDate = parseDate(minDate);
                  const parsedMaxDate = parseDate(maxDate);

                  if (parsedMinDate && date.getFullYear() < parsedMinDate.getFullYear()) {
                    return true;
                  }
                  if (parsedMaxDate && date.getFullYear() > parsedMaxDate.getFullYear()) {
                    return true;
                  }
                  return false;
                }}
                slotProps={{
                  textField: {
                    placeholder,
                    fullWidth: true,
                    error: !!errors,
                    className: classNames("form-control", classInput, {
                      "border-danger": errors,
                    }),
                    onBlur: () => {
                      const currentDate = parseDate(value);
                      const parsedMinDate = parseDate(minDate);
                      const parsedMaxDate = parseDate(maxDate);

                      // If the value is completely cleared, leave it empty
                      if (!value) {
                        onChange(null);
                        if (onChangeExtra) onChangeExtra(null);
                        return;
                      }

                      let correctedDate = currentDate;

                      // If date is before minDate, set to minDate
                      if (parsedMinDate && isBefore(startOfDay(currentDate), startOfDay(parsedMinDate))) {
                        correctedDate = parsedMinDate;
                      }
                      // If date is after maxDate, set to maxDate
                      else if (parsedMaxDate && isAfter(startOfDay(currentDate), startOfDay(parsedMaxDate))) {
                        correctedDate = parsedMaxDate;
                      }

                      // Update if correction was made
                      if (correctedDate !== currentDate) {
                        const formattedDate = format(correctedDate, DATE_FORMAT.DATE);
                        onChange(formattedDate);
                        if (onChangeExtra) onChangeExtra(formattedDate);
                      }
                    },

                    InputProps: {
                      style: {
                        height: "38px",
                        fontSize: "14px",
                      },
                    },
                  },
                }}
              />
            )}
          />
        </LocalizationProvider>
      </ThemeProvider>

      {errors && <div className="text-danger font-12 position-absolute">{errors}</div>}
    </div>
  );
};

export default DatePickerCustomField;
